import { useEffect, useMemo, useRef, useState } from "react";
import { Add, Download, Upload } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useNotification } from "../../../../../componentes/notificationProvider";
import NewAlertModal from "../../../../../componentes/newAlertModal";
import FlowHeader from "../flowHeader";
import FlowTable from "../flowTable";
import SectionBar from "../sectionBar";
import { useTabStyles } from "../styles";
import api from "../../../../../services/api";

import {
  MassEntity,
  MassErrors,
  MassFlowConfig,
  MassRecord,
  MassRow,
  MassValues,
} from "./types";

const hasErrors = (errors: MassErrors): boolean =>
  Object.keys(errors).length > 0;

const isNewRow = (row: MassRow) => row?._isNew;

const isPendingRow = (row: MassRow) => row?._isNew || row?._isEdited;

const noun = (entity: MassEntity, count: number) =>
  `${count} ${count === 1 ? entity.singular : entity.plural}`;

const participle = (entity: MassEntity, stem: string, count: number) =>
  `${stem}${entity.gender === "f" ? "a" : "o"}${count === 1 ? "" : "s"}`;

const phrase = (entity: MassEntity, count: number, stem: string) =>
  `${noun(entity, count)} ${participle(entity, stem, count)}`;

const selectionLabel = (entity: MassEntity, count: number) => {
  const name = count === 1 ? entity.singular : entity.plural;
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${participle(
    entity,
    "selecionad",
    count,
  )}`;
};

const toValues = (source: MassValues, fields: string[]): MassValues =>
  fields.reduce<MassValues>((values, field) => {
    values[field] = (source[field] ?? "").toString().trim();
    return values;
  }, {});

const hasChanges = (
  row: MassRow,
  original: MassValues | undefined,
  fields: string[],
) =>
  Boolean(original) &&
  fields.some(
    (field) =>
      (row[field] ?? "").toString().trim() !==
      (original?.[field] ?? "").toString().trim(),
  );

const newRow = (config: MassFlowConfig, values?: MassValues): MassRow => {
  const data = { ...config.emptyValues, ...values };
  return {
    ...data,
    id: uuidv4(),
    _isNew: true,
    _isEdited: false,
    selected: false,
    _errors: config.validate(data),
  };
};

const existingRow = (config: MassFlowConfig, record: MassRecord): MassRow => {
  const data = { ...config.emptyValues, ...toValues(record, config.fields) };
  return {
    ...data,
    id: uuidv4(),
    dbId: record.id,
    _original: { ...data },
    _isNew: false,
    _isEdited: false,
    selected: false,
    _errors: config.validate(data),
  };
};

export interface MassFlowProps {
  config: MassFlowConfig;
  records: MassRecord[];
  search: string;
  onSearchChange: (value: string) => void;
  onBack: () => void;
  onSaved: () => Promise<void> | void;
}

export default function MassFlow(props: MassFlowProps) {
  const { config, records, search, onSearchChange, onBack, onSaved } = props;
  const { entity, fields } = config;

  const [rows, setRows] = useState<MassRow[]>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [tableKey, setTableKey] = useState<number>(0);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openBackModal, setOpenBackModal] = useState<boolean>(false);

  const theme = useTheme();
  const styles = useTabStyles(theme);
  const notify = useNotification();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const rebuildRef = useRef<boolean>(false);

  const refreshRows = () => setRows((prev) => [...prev]);

  useEffect(() => {
    if (search.trim()) onSearchChange("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRows((prev) => {
      const novas = prev.filter(isNewRow);
      const existentes = records.map((record) => existingRow(config, record));

      if (rebuildRef.current) {
        rebuildRef.current = false;
        return [...existentes, ...novas];
      }

      const anteriores = new Map(
        prev
          .filter((row) => !isNewRow(row))
          .map((row) => [String(row.dbId), row]),
      );

      return [
        ...existentes.map((atual) => {
          const anterior = anteriores.get(String(atual.dbId));
          if (!anterior) return atual;

          return {
            ...anterior,
            _original: atual._original,
            _isEdited: hasChanges(anterior, atual._original, fields),
          };
        }),
        ...novas,
      ];
    });
    setTableKey((key) => key + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  const rowsForDisplay = useMemo(() => {
    if (!search.trim()) return rows;

    const searchLower = search.toLowerCase();
    return rows.filter((row) =>
      fields.some((field) =>
        String(row[field] ?? "")
          .toLowerCase()
          .includes(searchLower),
      ),
    );
  }, [fields, rows, search]);

  const selectedRows = useMemo(
    () => rows.filter((row) => row.selected),
    [rows],
  );
  const pendingRows = useMemo(() => rows.filter(isPendingRow), [rows]);
  const savableRows = useMemo(
    () => pendingRows.filter((row) => !hasErrors(row._errors)),
    [pendingRows],
  );
  const invalidCount = useMemo(
    () => rows.filter((row) => hasErrors(row._errors)).length,
    [rows],
  );

  const handleClearSelection = () => {
    rows.forEach((row) => {
      row.selected = false;
    });
    refreshRows();
    setTableKey((key) => key + 1);
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, newRow(config)]);
    setTableKey((key) => key + 1);
  };

  const handleCellChange = (row: MassRow) => {
    row._errors = config.validate(row);
    if (!row?._isNew) row._isEdited = hasChanges(row, row._original, fields);
    refreshRows();
  };

  const handleImportCsv = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !config.csv) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      notify("Apenas arquivos .csv são permitidos.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = config.csv!.parse(String(reader.result ?? ""));
        if (imported.length === 0) {
          notify(`Nenhum registro encontrado no arquivo.`, "warning");
          return;
        }

        setRows((prev) => [
          ...prev,
          ...imported.map((values) => newRow(config, values)),
        ]);
        setTableKey((key) => key + 1);
        notify(
          `${phrase(entity, imported.length, "importad")} do CSV.`,
          "success",
        );
      } catch (error: any) {
        notify(error?.message || "Erro ao ler o arquivo CSV", "error");
      }
    };
    reader.onerror = () => notify("Erro ao ler o arquivo CSV", "error");
    reader.readAsText(file, "UTF-8");
  };

  const handleSave = async () => {
    if (savableRows.length === 0) {
      notify(
        invalidCount > 0
          ? "Corrija os campos obrigatórios antes de salvar."
          : "Nenhuma alteração para salvar.",
        "warning",
        6000,
      );
      return;
    }

    const paraCriar = savableRows.filter(isNewRow);
    const paraEditar = savableRows.filter((row) => !isNewRow(row));
    const comErro = pendingRows.length - savableRows.length;

    try {
      setIsSaving(true);

      if (paraCriar.length > 0) {
        await api.post(
          config.endpoint,
          paraCriar.map((row) => toValues(row, fields)),
        );

        const criados = new Set(paraCriar.map((row) => row.id));
        setRows((prev) => prev.filter((row) => !criados.has(row.id)));
      }

      if (paraEditar.length > 0) {
        await api.put(
          config.endpoint,
          paraEditar.map((row) => ({
            id: row.dbId,
            ...toValues(row, fields),
          })),
        );
      }

      notify(
        [
          paraCriar.length > 0
            ? phrase(entity, paraCriar.length, "cadastrad")
            : "",
          paraEditar.length > 0
            ? phrase(entity, paraEditar.length, "atualizad")
            : "",
        ]
          .filter(Boolean)
          .join(" e ") + " com sucesso!",
        "success",
        6000,
      );

      if (comErro > 0) {
        notify(
          `${noun(entity, comErro)} com dados inválidos não ${
            comErro === 1 ? "foi" : "foram"
          } ${participle(entity, "salv", comErro)}.`,
          "warning",
          8000,
        );
      }

      await onSaved();

      if (comErro === 0) onBack();
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          `Não foi possível salvar ${entity.plural}.`,
        "error",
        8000,
      );
      await onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSelected = async () => {
    const naPlanilha = selectedRows.filter((row) => !isNewRow(row));
    const ids = new Set(selectedRows.map((row) => row.id));

    const edicoesDescartadas = rows.filter(
      (row) => row._isEdited && !ids.has(row.id),
    ).length;

    try {
      setIsSaving(true);

      if (naPlanilha.length > 0) {
        await api.delete(config.endpoint, {
          data: { ids: naPlanilha.map((row) => row.dbId) },
        });
        rebuildRef.current = true;
      }

      setRows((prev) => prev.filter((row) => !ids.has(row.id)));
      setTableKey((key) => key + 1);

      notify(
        `${phrase(entity, selectedRows.length, "removid")}${
          naPlanilha.length > 0 ? " da planilha" : ""
        }.`,
        "success",
        4000,
      );

      if (naPlanilha.length > 0) {
        if (edicoesDescartadas > 0) {
          notify(
            `${noun(entity, edicoesDescartadas)} com alterações não salvas ${
              edicoesDescartadas === 1 ? "foi" : "foram"
            } ${participle(
              entity,
              "recarregad",
              edicoesDescartadas,
            )} após a exclusão. Refaça a edição.`,
            "warning",
            8000,
          );
        }
        await onSaved();
      }
    } catch (error: any) {
      console.error(error);
      notify(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          `Não foi possível remover ${entity.plural}.`,
        "error",
        8000,
      );
    } finally {
      setIsSaving(false);
      setOpenDeleteModal(false);
    }
  };

  const handleRequestBack = () => {
    if (pendingRows.length > 0) {
      setOpenBackModal(true);
      return;
    }
    onBack();
  };

  return (
    <>
      <FlowHeader
        isEditable
        search={search}
        onSearchChange={onSearchChange}
        searchPlaceholder={config.searchPlaceholder}
        savingChanges={isSaving}
        disableSave={savableRows.length === 0}
        onCancelEditMode={handleRequestBack}
        onSaveChanges={handleSave}
        startContent={
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            <Chip size="small" label={`${rows.length} linha(s)`} />
            {pendingRows.length > 0 && (
              <Chip
                size="small"
                color="warning"
                variant="outlined"
                label={`${pendingRows.length} pendente(s)`}
              />
            )}
            {invalidCount > 0 && (
              <Chip
                size="small"
                color="error"
                variant="outlined"
                label={`${invalidCount} com erro`}
              />
            )}

            {config.csv && (
              <>
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Baixar o modelo de planilha para a importação">
                  <span>
                    <Button
                      size="small"
                      variant="text"
                      disabled={isSaving}
                      onClick={config.csv.onDownloadTemplate}
                      startIcon={<Download fontSize="small" />}
                    >
                      Baixar template
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </Stack>
        }
        extraActions={
          <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
            {config.csv && (
              <>
                <Tooltip title="Importar de um arquivo CSV">
                  <span>
                    <IconButton
                      size="small"
                      sx={styles.iconButtonAction}
                      disabled={isSaving}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={handleImportCsv}
                />
              </>
            )}

            <Tooltip title="Adicionar linha">
              <span>
                <IconButton
                  size="small"
                  sx={styles.iconButtonAction}
                  disabled={isSaving}
                  onClick={handleAddRow}
                >
                  <Add fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      <Divider flexItem sx={{ my: 1, borderColor: theme.palette.divider }} />

      <Box sx={styles.boxTable}>
        <FlowTable<MassRow>
          key={`${config.tableId}-${tableKey}`}
          id={config.tableId}
          rows={rowsForDisplay}
          columns={config.columns}
          isEditable={!isSaving}
          selectable
          onSelectionChange={refreshRows}
          onCellChange={handleCellChange}
          rowClassName={(row) => (isNewRow(row) ? "row-new" : undefined)}
        />
      </Box>

      <SectionBar
        selectedCount={selectedRows.length}
        label={selectionLabel(entity, selectedRows.length)}
        disabled={isSaving}
        onDelete={() => setOpenDeleteModal(true)}
        onClearSelection={handleClearSelection}
      />

      <NewAlertModal
        openModal={openDeleteModal}
        setOpenModal={setOpenDeleteModal}
        textContent={`Tem certeza que deseja excluir ${noun(
          entity,
          selectedRows.length,
        )}?`}
        handleButton={handleDeleteSelected}
        severity={"error"}
        singleButton={false}
      />

      <NewAlertModal
        openModal={openBackModal}
        setOpenModal={setOpenBackModal}
        textContent="Existem alterações não salvas. Se sair agora, essas alterações serão perdidas."
        buttonText="Descartar"
        buttonCancelText="Cancelar"
        handleButton={() => {
          setOpenBackModal(false);
          onBack();
        }}
        severity={"warning"}
        singleButton={false}
      />
    </>
  );
}
